import { anonymizeImage, type FaceRegion } from "./blur";
import type { AnonymizeRequest, AnonymizeResponse } from "./worker";

export type { FaceRegion };

const WORKER_TIMEOUT_MS = 5_000;

let worker: Worker | null = null;
let workerBroken = false;
let nextRequestId = 0;
const pending = new Map<
  number,
  { resolve: (image: Blob) => void; reject: (error: Error) => void }
>();

/**
 * Returns a copy of the captured frame with every detected face blurred and
 * outlined. Frames with no detected face are returned as they are. The heavy
 * pixel work runs in a Web Worker so the kiosk UI and turnstile flow are never
 * blocked; browsers without worker canvas support fall back to an idle
 * main-thread task.
 */
export async function anonymizeFaceCapture(
  image: Blob,
  boxes: FaceRegion[],
  frame: { width: number; height: number },
): Promise<Blob> {
  if (boxes.length === 0) return image;

  if (typeof createImageBitmap !== "function") {
    throw new Error("createImageBitmap is unavailable.");
  }

  if (canUseWorker()) {
    try {
      return await anonymizeInWorker(image, boxes, frame);
    } catch {
      disposeWorker();
      workerBroken = true;
    }
  }

  await waitForIdle();
  return anonymizeImage(image, boxes, frame);
}

function canUseWorker(): boolean {
  return (
    !workerBroken &&
    typeof Worker !== "undefined" &&
    typeof OffscreenCanvas !== "undefined"
  );
}

function anonymizeInWorker(
  image: Blob,
  boxes: FaceRegion[],
  frame: { width: number; height: number },
): Promise<Blob> {
  const activeWorker = getWorker();
  const id = ++nextRequestId;

  return new Promise<Blob>((resolve, reject) => {
    const timeoutId = window.setTimeout(() => {
      pending.delete(id);
      reject(new Error("Anonymization worker timed out."));
    }, WORKER_TIMEOUT_MS);

    pending.set(id, {
      resolve: (result) => {
        window.clearTimeout(timeoutId);
        resolve(result);
      },
      reject: (error) => {
        window.clearTimeout(timeoutId);
        reject(error);
      },
    });

    const request: AnonymizeRequest = { id, image, boxes, frame };
    activeWorker.postMessage(request);
  });
}

function getWorker(): Worker {
  if (worker) return worker;

  worker = new Worker(new URL("./worker.ts", import.meta.url), {
    type: "module",
  });
  worker.onmessage = (event: MessageEvent<AnonymizeResponse>) => {
    const response = event.data;
    const request = pending.get(response.id);
    if (!request) return;
    pending.delete(response.id);
    if (response.ok) request.resolve(response.image);
    else request.reject(new Error(response.message));
  };
  worker.onerror = () => {
    rejectAllPending(new Error("Anonymization worker crashed."));
    disposeWorker();
    workerBroken = true;
  };
  return worker;
}

function disposeWorker(): void {
  worker?.terminate();
  worker = null;
  rejectAllPending(new Error("Anonymization worker was stopped."));
}

function rejectAllPending(error: Error): void {
  for (const request of pending.values()) request.reject(error);
  pending.clear();
}

function waitForIdle(): Promise<void> {
  return new Promise((resolve) => {
    if (typeof window.requestIdleCallback === "function") {
      window.requestIdleCallback(() => resolve(), { timeout: 1_000 });
    } else {
      window.setTimeout(resolve, 0);
    }
  });
}
